import burstImg from '~/assets/images/catchup-burst.svg'
import cometImg from '~/assets/images/catchup-comet.svg'
import sideWaveImg from '~/assets/images/catchup-side-wave.svg'
import type { CatchupPhase } from '~/hooks/use-catchup-animation'
import { motion } from '~/lib/motion'

/**
 * The three glow decorations layered over the post-jump catch-up run, per
 * the design's 追趕進度動畫 three-frame sequence: side light waves at the
 * viewport edges, a comet trailing the moving progress head, and a burst at
 * the final resting spot. Enter/exit timing follows the prototype's Smart
 * Animate params (0.3s ease-out).
 */

const GLOW_TRANSITION = { duration: 0.3, ease: 'easeOut' } as const

/**
 * Wave x-offsets per phase, from the prototype frames (393pt viewport):
 * parked at the edge as a sliver, slid inward during the sprint, retreated
 * at the end.
 */
const WAVE_X: Record<Exclude<CatchupPhase, 'idle'>, number> = {
  approach: -183,
  sprint: -45,
  settle: -167,
}

/**
 * Two full-height light waves fixed to the viewport edges. The pink-to-clear
 * gradient only reads as warm white through additive blending over the page
 * (`plus-lighter`), so each wave blends directly against the root backdrop —
 * no wrapper, since any transformed/stacking wrapper would isolate the blend.
 * The right wave is the same asset rotated 180°.
 */
export function SideLightWaves(props: { phase: CatchupPhase }) {
  const x = props.phase === 'idle' ? WAVE_X.approach : WAVE_X[props.phase]
  return (
    <>
      <motion.img
        src={sideWaveImg}
        alt=""
        className="pointer-events-none fixed inset-y-0 left-0 z-[50] h-full w-[221px] max-w-none mix-blend-plus-lighter"
        initial={{ opacity: 0, x: WAVE_X.approach }}
        animate={{ opacity: 1, x }}
        exit={{ opacity: 0 }}
        transition={GLOW_TRANSITION}
      />
      <motion.img
        src={sideWaveImg}
        alt=""
        className="pointer-events-none fixed inset-y-0 right-0 z-[50] h-full w-[221px] max-w-none mix-blend-plus-lighter"
        initial={{ opacity: 0, x: -WAVE_X.approach, rotate: 180 }}
        animate={{ opacity: 1, x: -x, rotate: 180 }}
        exit={{ opacity: 0 }}
        transition={GLOW_TRANSITION}
      />
    </>
  )
}

/**
 * The small glow seeded on the progress bubble while the page approaches
 * the old score's position (prototype frame 1).
 */
export function CatchupSeedGlow(props: { centerY: number }) {
  return (
    <motion.div
      className="pointer-events-none absolute left-1/2 h-16 w-[49px] -translate-x-1/2 blur-[8px]"
      style={{
        top: props.centerY - 32,
        background:
          'radial-gradient(50% 50% at 50% 50%, rgba(112, 255, 183, 0.81) 0%, rgba(88, 233, 239, 0) 100%)',
      }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={GLOW_TRANSITION}
    />
  )
}

/**
 * Where the comet's head circle sits inside the asset's 76×236 box — the
 * tail streams upward, trailing the head as the bar grows downward.
 */
const COMET_HEAD_CENTER_Y = 203

/** The glow comet on the progress head; exists only while the bar moves. */
export function ProgressComet(props: { centerY: number }) {
  return (
    <motion.img
      src={cometImg}
      alt=""
      className="pointer-events-none absolute left-1/2 h-[236px] w-[76px] max-w-none -translate-x-1/2"
      style={{ top: props.centerY - COMET_HEAD_CENTER_Y }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0, transition: { duration: 0.15 } }}
      transition={GLOW_TRANSITION}
    />
  )
}

/** The burst played at the bubble's final resting spot (prototype frame 3). */
export function EndingBurst(props: { centerY: number }) {
  return (
    <motion.img
      src={burstImg}
      alt=""
      className="pointer-events-none absolute left-1/2 size-16 max-w-none"
      style={{ top: props.centerY - 32, x: '-50%' }}
      initial={{ opacity: 0, scale: 0.5 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0 }}
      transition={GLOW_TRANSITION}
    />
  )
}
